import { inject, Injectable } from '@angular/core';
import {
  filter,
  interval,
  map,
  Observable,
  of,
  pairwise,
  ReplaySubject,
  shareReplay,
  switchMap,
  take,
  tap,
} from 'rxjs';
import { LoginDto, LoginResult, RefreshDto, TokenPairDto } from './model';
import { InternalAuthApiService } from './internal-auth-api.service';
import { AuthenticationService, LogoutOption } from '@ups/security';

const LOCAL_STORAGE_KEY = 'InternalAuth:State';

const warn = (...args: unknown[]) => console.warn('InternalAuth:', ...args);
const log = (...args: unknown[]) => console.log('InternalAuth:', ...args);
// const trace = (...args: unknown[]) => console.trace('InternalAuth:', ...args);

type States =
  | { state: 'init' }
  | { state: 'unauth' }
  | { state: 'auth'; token: TokenPairDto };

let INSTANCE_EXISTS = false;

@Injectable()
export class InternalAuthenticationService implements AuthenticationService {
  private internalAuthApi = inject(InternalAuthApiService);

  private state$ = new ReplaySubject<States>(1);

  private setState(state: States) {
    let logState = state;
    if ('token' in logState) {
      logState = {
        ...logState,
        token: {
          accessToken: `${logState.token.accessToken.slice(0, 48)}...`,
          refreshToken: `${logState.token.refreshToken.slice(0, 48)}...`,
          refreshTokenExpiresAt: logState.token.refreshTokenExpiresAt,
        },
      };
    }
    log('SET', logState);

    queueMicrotask(() => this.state$.next(state));
  }

  private computedState$ = this.state$.pipe(
    map((x) => {
      switch (x.state) {
        case 'init':
        case 'unauth':
          return x;
        case 'auth': {
          const [, payloadEncoded] = x.token.accessToken.split('.');
          const payloadJson = atob(
            payloadEncoded.replaceAll('-', '+').replaceAll('_', '/')
          );
          const payload = JSON.parse(payloadJson) as { exp: number };
          return { ...x, payload };
        }
      }
    })
  );

  private _loadSub = this.state$
    .pipe(filter((x) => x.state === 'init'))
    .subscribe(() => {
      log('Load state');

      const value = localStorage.getItem(LOCAL_STORAGE_KEY);

      try {
        if (!value) throw new Error('No saved state');
        const parsed = JSON.parse(value) as States;

        if (parsed.state === 'init') throw new Error('Invalid state');

        this.setState(parsed);
        log('State loaded');
      } catch (e) {
        log('Load state err', e);

        this.setState({ state: 'unauth' });
      }
    });

  private _saveSub = this.state$
    .pipe(
      pairwise(),
      filter(([prev]) => prev?.state !== 'init')
    )
    .subscribe(([, next]) => this.saveState(next));

  private _refreshSub = this.computedState$
    .pipe(
      switchMap((x) => {
        if (x.state === 'auth') {
          const refreshIn = Math.max(
            0,
            x.payload.exp * 1000 - Date.now() - 5 * 60 * 1000
          ); // 5 minutes before expiration

          log('Refresh in:', refreshIn / 1000);

          return interval(refreshIn).pipe(
            take(1),
            tap(() => this.refresh({ refreshToken: x.token.refreshToken }))
          );
        } else {
          return of(null);
        }
      })
    )
    .subscribe();

  private saveState(state: States) {
    // Log only the state kind — never the raw state, which carries the tokens
    // (setState masks them; this path must not defeat that).
    log('SAVE', state.state);
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(state));
  }

  public claims$ = this.computedState$.pipe(
    map((x) => (x.state === 'auth' ? x.payload : null))
  );

  public identity$ = this.claims$.pipe(
    map((x) =>
      x
        ? ({
            kind: 'id',
            id: x['http://security.universalplant.com/user-id'],
          } as const)
        : null
    )
  );

  public isAuthenticated$ = this.state$.pipe(
    filter(({ state }) => state === 'auth' || state === 'unauth'),
    map((x) => x.state === 'auth')
  );

  constructor() {
    if (INSTANCE_EXISTS) {
      throw new Error(
        'InternalAuthenticationService was constructed multiple times'
      );
    }
    INSTANCE_EXISTS = true;

    this.setState({ state: 'init' });
  }

  getAccessTokenSilently(): Observable<string> {
    return this.computedState$.pipe(
      filter((x) => x.state === 'auth'),
      // filter out AT if it has less than 1 minute left
      filter((x) => x.payload.exp * 1000 - 60_000 > Date.now()),
      map((x) => x.token.accessToken)
    );
  }

  login(data: LoginDto): Observable<LoginResult> {
    return this.internalAuthApi.login(data).pipe(
      tap((x) => {
        if (x.result === LoginResult.Success) {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          this.setState({ state: 'auth', token: x.token! });
        }
      }),
      map((x) => x.result),
      shareReplay(1)
    );
  }

  logout(options: LogoutOption): void {
    const afterLogout = () => {
      this.saveState({ state: 'unauth' });
      location.href = options.returnTo ?? location.origin;
    };

    this.state$
      .pipe(
        switchMap((x) =>
          x.state === 'auth'
            ? this.internalAuthApi.logout(x.token.accessToken)
            : of(null)
        )
      )
      .subscribe({
        next: () => afterLogout(),
        error: () => afterLogout(),
      });
  }

  refresh(data: RefreshDto) {
    log('Refreshing');

    this.internalAuthApi.refresh(data).subscribe({
      next: (x) => {
        this.setState({ state: 'auth', token: x });
      },
      error: () => {
        warn('refresh failed');
        this.setState({ state: 'unauth' });
      },
    });
  }
}
